import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: process.env.REACT_APP_API_TIMEOUT || 30000
});

// Add token to headers if available
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('jwtToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const getLoginUrl = async () => {
  const response = await apiClient.get('/api/auth/login-url');
  return response.data.loginURL;
};

export const exchangeToken = async (requestToken) => {
  const response = await apiClient.post('/api/auth/callback', { requestToken });
  return response.data;
};

export const checkSessionStatus = async () => {
  const response = await apiClient.get('/api/auth/session-status');
  return response.data;
};

export const testKiteToken = async () => {
  const response = await apiClient.post('/api/kite/test-order');
  return response.data;
};

export const getUserProfile = async () => {
  const response = await apiClient.get('/api/kite/profile');
  return response.data;
};

export const getHoldings = async () => {
  const response = await apiClient.get('/api/kite/holdings');
  return response.data;
};

export const getPositions = async () => {
  const response = await apiClient.get('/api/kite/positions');
  return response.data;
};

export const getOrders = async () => {
  const response = await apiClient.get('/api/kite/orders');
  return response.data;
};

export const placeOrder = async (orderData) => {
  const response = await apiClient.post('/api/kite/order', orderData);
  return response.data;
};

export const getTopVolumeRows = async (amount, maxQtyMultiplier) => {
  const response = await apiClient.get('/api/analysis/top-volume', { params: { amount, maxQtyMultiplier } });
  return response.data;
};

export const createAnalysisOrder = async (orderData) => {
  const response = await apiClient.post('/api/analysis/create-order', orderData);
  return response.data;
};

export const squareOffAnalysisSymbol = async (symbol) => {
  const response = await apiClient.post('/api/analysis/square-off-symbol', { symbol });
  return response.data;
};

export const getAnalysisHistory = async (symbol) => {
  const response = await apiClient.get(`/api/analysis/history/${encodeURIComponent(symbol)}`);
  return response.data;
};

export const downloadAnalysisHistory = async () => {
  const response = await apiClient.get('/api/analysis/download-history', { responseType: 'blob' });
  return response;
};

export const cleanupAnalysisData = async () => {
  const response = await apiClient.post('/api/analysis/cleanup');
  return response.data;
};

export const getMomentumSuggestions = async () => {
  const response = await apiClient.get('/api/analysis/suggestions');
  return response.data;
};

export const getHomeTopGainers = async () => {
  const response = await apiClient.get('/api/home/top-gainers');
  return response.data;
};

export const getHomeTopLoosers = async () => {
  const response = await apiClient.get('/api/home/top-loosers');
  return response.data;
};

export const getHomeTopVolumes = async () => {
  const response = await apiClient.get('/api/home/top-volumes');
  return response.data;
};

export const getHomeFallingGainers = async () => {
  const response = await apiClient.get('/api/home/falling-gainers');
  return response.data;
};

export const getHomeRisingLoosers = async () => {
  const response = await apiClient.get('/api/home/rising-loosers');
  return response.data;
};

export const placeHomeOrder = async (orderData) => {
  const response = await apiClient.post('/api/home/order', orderData);
  return response.data;
};

export const placeAnalysisTradeOrder = async (orderData) => {
  const response = await apiClient.post('/api/analysis/trade-order', orderData);
  return response.data;
};

export const getAnalysisPositions = async (amount, maxQtyMultiplier) => {
  const response = await apiClient.get('/api/analysis/positions', { params: { amount, maxQtyMultiplier } });
  return response.data;
};

export const setAnalysisTradedValue = async (value) => {
  const response = await apiClient.post('/api/analysis/traded-value', { value });
  return response.data;
};

export const setAnalysisMaxQtyMultiplier = async (multiplier) => {
  const response = await apiClient.post('/api/analysis/max-qty-multiplier', { multiplier });
  return response.data;
};

export const getAutopilotStatus = async () => {
  const response = await apiClient.get('/api/analysis/autopilot-status');
  return response.data;
};

export const setAutopilotToggle = async (enabled, type) => {
  const response = await apiClient.post('/api/analysis/autopilot-toggle', { enabled, type });
  return response.data;
};

export const getTestMode = async () => {
  const response = await apiClient.get('/api/analysis/test-mode');
  return response.data;
};

export const getTestModeScope = async (product) => {
  const response = await apiClient.get('/api/analysis/test-mode/scope', { params: { product } });
  return response.data;
};

export const setTestMode = async (enabled, choice, product) => {
  const response = await apiClient.post('/api/analysis/test-mode', { enabled, choice, product });
  return response.data;
};

export const getManagePnL = async () => {
  const response = await apiClient.get('/api/analysis/manage-pnl');
  return response.data;
};

export const setManagePnL = async (enabled, settings) => {
  const response = await apiClient.post('/api/analysis/manage-pnl', { enabled, settings });
  return response.data;
};

export const searchInstruments = async (q) => {
  const response = await apiClient.get(`/api/instruments/search?q=${encodeURIComponent(q)}`);
  return response.data;
};

export const getInstruments = async () => {
  const response = await apiClient.get('/api/kite/instruments-cache');
  return response.data;
};

export const getStockConfig = async () => {
  const response = await apiClient.get('/api/config');
  return response.data;
};

export const addStockConfig = async (stock) => {
  const response = await apiClient.post('/api/config/stock', stock);
  return response.data;
};

export const deleteStockConfig = async (symbol) => {
  const response = await apiClient.delete(`/api/config/stock/${symbol}`);
  return response.data;
};

export const patchStockConfig = async (symbol, data) => {
  const response = await apiClient.patch(`/api/config/stock/${symbol}`, data);
  return response.data;
};

export const getGlobalSettings = async () => {
  const response = await apiClient.get('/api/config/global-settings');
  return response.data;
};

export const saveGlobalSettings = async (settings) => {
  const response = await apiClient.put('/api/config/global-settings', settings);
  return response.data;
};

export const manualSquareOffAll = async () => {
  const response = await apiClient.post('/api/config/square-off-all');
  return response.data;
};

export const getGlobalPauseStatus = async () => {
  const response = await apiClient.get('/api/config/global-pause-status');
  return response.data;
};

export const getPnLStatus = async () => {
  const response = await apiClient.get('/api/config/pnl-status');
  return response.data;
};

export const reactivateAlgos = async () => {
  const response = await apiClient.post('/api/config/reactivate-algos');
  return response.data;
};

export const toggleAlgoFavorite = async (id, favorite) => {
  const response = await apiClient.patch(`/api/algos/${id}/favorite`, { favorite });
  return response.data;
};

export const toggleMyStrategyFavorite = async (type, favorite) => {
  const response = await apiClient.patch(`/api/algos/my-strategies/${type}/favorite`, { favorite });
  return response.data;
};

export const toggleMyStrategyAutopilotEligible = async (type, autopilotEligible) => {
  const response = await apiClient.patch(`/api/algos/my-strategies/${type}/autopilot-eligible`, { autopilotEligible });
  return response.data;
};

export const getAlgos = async () => {
  const response = await apiClient.get('/api/algos');
  return response.data;
};

export const getTrades = async (algoId, date) => {
  const response = await apiClient.get(`/api/trades${algoId ? `/${algoId}` : ''}`, { params: date ? { date } : {} });
  return response.data;
};

export const getTradeDates = async (algoId) => {
  const response = await apiClient.get(`/api/trades/${algoId}/dates`);
  return response.data;
};

export const getIntradayHistory = async () => {
  const response = await apiClient.get('/api/intraday');
  return response.data;
};

export const refreshIntradayHistory = async () => {
  const response = await apiClient.post('/api/intraday/refresh');
  return response.data;
};

export const createAlgo = async (algo) => {
  const response = await apiClient.post('/api/algos', algo);
  return response.data;
};

export const updateAlgo = async (id, algo) => {
  const response = await apiClient.put(`/api/algos/${id}`, algo);
  return response.data;
};

export const deleteAlgo = async (id) => {
  const response = await apiClient.delete(`/api/algos/${id}`);
  return response.data;
};

export const getQuote = async (symbols) => {
  const response = await apiClient.get(`/api/quote?symbols=${symbols.join(',')}`);
  return response.data;
};

export const runDemoBacktest = async (algoId) => {
  const response = await apiClient.post(`/api/demo/${algoId}`);
  return response.data;
};

export const getLocalProfile = async () => {
  const response = await apiClient.get('/api/profile');
  return response.data;
};

export const saveLocalProfile = async (data) => {
  const response = await apiClient.post('/api/profile', data);
  return response.data;
};

export const getHistoricalData = async (symbol, interval, fromDate, toDate, token) => {
  const response = await apiClient.get('/api/kite/historical', {
    params: { symbol, interval, fromDate, toDate, ...(token && { token }) }
  });
  return response.data;
};

export const getHistoricalDataList = async () => {
  const response = await apiClient.get('/api/analysis/historical-data');
  return response.data;
};

export const saveHistoricalData = async (data) => {
  const response = await apiClient.post('/api/analysis/save-historical', data);
  return response.data;
};

export const getStrategies = async () => {
  const response = await apiClient.get('/api/algos/strategies');
  return response.data;
};

export const updateStrategy = async (type, data) => {
  const response = await apiClient.put(`/api/algos/strategies/${type}`, data);
  return response.data;
};

export const getMyStrategies = async () => {
  const response = await apiClient.get('/api/algos/my-strategies');
  return response.data;
};

export const updateMyStrategy = async (type, data) => {
  const response = await apiClient.put(`/api/algos/my-strategies/${type}`, data);
  return response.data;
};

export const getMyStrategyTrades = async (type, date) => {
  const response = await apiClient.get(`/api/algos/my-strategies/${type}/trades`, { params: date ? { date } : {} });
  return response.data;
};

export const getMyStrategyTradeDates = async (type) => {
  const response = await apiClient.get(`/api/algos/my-strategies/${type}/dates`);
  return response.data;
};

export const squareOffMyStrategyTrade = async (type, tradeId) => {
  const response = await apiClient.post(`/api/algos/my-strategies/${type}/trades/${tradeId}/square-off`);
  return response.data;
};

export const stopAlgoTrade = async (type, symbol) => {
  const response = await apiClient.post(`/api/algos/my-strategies/${type}/stop-trade`, { symbol });
  return response.data;
};

export const noReverseTrade = async (type, symbol) => {
  const response = await apiClient.post(`/api/algos/my-strategies/${type}/no-reverse`, { symbol });
  return response.data;
};

export const getSBSScanLog = async () => {
  const response = await apiClient.get('/api/algos/my-strategies/slowButSteady/scan-log');
  return response.data;
};

export const getBahubaliScanLog = async () => {
  const response = await apiClient.get('/api/algos/my-strategies/bahubali/scan-log');
  return response.data;
};

export const deleteHistoricalData = async (id) => {
  const response = await apiClient.delete(`/api/analysis/historical-data/${id}`);
  return response.data;
};

export const getBacktestDates = async () => {
  const response = await apiClient.get('/api/backtest-data/dates');
  return response.data;
};

// ── User-downloaded historical data as backtest source ────────────────────────

// Dates that have at least one candle in the user's saved historical data
export const getUserDataDates = async () => {
  const response = await apiClient.get('/api/analysis/historical-data');
  const entries  = response.data.data || [];
  const dates    = new Set();
  for (const entry of entries) {
    for (const c of (entry.candles || [])) {
      dates.add(new Date(c[0]).toISOString().substring(0, 10));
    }
  }
  return { dates: Array.from(dates).sort() };
};

// Raw, unfiltered historical-data entries — full multi-day candle array per
// symbol, not cut down to a single date. Used by Fingers Crossed's backtest
// to build its relative-volume baseline from the trading days prior to the
// backtest date (mirroring what the live pre-market scheduler computes).
export const getUserDataRawEntries = async () => {
  const response = await apiClient.get('/api/analysis/historical-data');
  return response.data.data || [];
};

// Candles from saved historical data — same shape as getBacktestCandles
// symbols=null returns ALL symbols that have data for that date
export const getUserDataCandles = async (date, symbols = null) => {
  const response = await apiClient.get('/api/analysis/historical-data');
  const entries  = response.data.data || [];
  const candles  = {};

  for (const entry of entries) {
    const sym = entry.symbol;
    if (symbols && symbols.length > 0 && !symbols.includes(sym)) continue;

    const dayCandles = (entry.candles || []).filter(c =>
      new Date(c[0]).toISOString().substring(0, 10) === date
    );
    if (!dayCandles.length) continue;

    if (!candles[sym]) {
      candles[sym] = dayCandles;
    } else {
      // Merge and sort if multiple entries cover the same symbol + date
      const merged = [...candles[sym], ...dayCandles];
      merged.sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime());
      // Deduplicate by timestamp
      candles[sym] = merged.filter((c, i) => i === 0 || c[0] !== merged[i - 1][0]);
    }
  }

  return { date, candles };
};

// Previous-day OHLC per symbol for the given date. Prefers the daily-interval
// prevDayByDate map auto-fetched alongside every "Pull More Data" historical
// download (see ConfigPage.js HistoricalDataPopup.handleGo), but falls back to
// deriving it from the entry's own intraday candles (close of the last candle
// dated strictly before `date`) when that map has no entry for the date —
// which happens when Kite hadn't posted the day-interval candle for the most
// recent pulled day yet at pull time. {[symbol]: {open,high,low,close}}
export const getUserDataPrevClose = async (date) => {
  const response = await apiClient.get('/api/analysis/historical-data');
  const entries  = response.data.data || [];
  const prevClose = {};

  for (const entry of entries) {
    const sym = entry.symbol;
    let pd = entry.prevDayByDate?.[date];

    if (!pd) {
      const before = (entry.candles || [])
        .filter(c => new Date(c[0]).toISOString().substring(0, 10) < date)
        .sort((a, b) => new Date(a[0]) - new Date(b[0]));
      if (before.length > 0) {
        const last = before[before.length - 1];
        pd = { open: last[1], high: last[2], low: last[3], close: last[4] };
      }
    }
    if (!pd) continue;

    // Prefer the most recently saved entry per symbol if multiple overlap
    if (!prevClose[sym] || new Date(entry.savedAt) > new Date(prevClose[sym]._savedAt)) {
      prevClose[sym] = { ...pd, _savedAt: entry.savedAt };
    }
  }
  for (const sym of Object.keys(prevClose)) delete prevClose[sym]._savedAt;

  return prevClose;
};

export const getBacktestCandles = async (date, symbols = null) => {
  const params = symbols && symbols.length ? `?symbols=${symbols.join(',')}` : '';
  const response = await apiClient.get(`/api/backtest-data/candles/${date}${params}`);
  return response.data;
};

export const getBacktestQuotes = async (date, timeStr, symbols) => {
  const params = new URLSearchParams({ time: timeStr, symbols: symbols.join(',') });
  const response = await apiClient.get(`/api/backtest-data/quotes/${date}?${params.toString()}`);
  return response.data;
};

export const simulateOBMBacktest = async (date, params, strategyType) => {
  const response = await apiClient.post('/api/backtest-data/simulate-obm', { date, params, strategyType });
  return response.data;
};

export const getNseStocksStatus = async () => {
  const response = await apiClient.get('/api/nse-stocks/status');
  return response.data;
};

export const getNseSectorMap = async () => {
  const response = await apiClient.get('/api/nse-stocks/sector-map');
  return response.data;
};

export const getNseLiquidityMap = async () => {
  const response = await apiClient.get('/api/nse-stocks/liquidity-map');
  return response.data;
};

export const triggerSnapshot = async (date) => {
  const response = await apiClient.post('/api/nse-stocks/snapshot', { date });
  return response.data;
};

export const downloadNseStocksData = async () => {
  const response = await apiClient.get('/api/nse-stocks/data', { responseType: 'blob' });
  const url = URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url; a.download = 'nse-stocks.json'; a.click();
  URL.revokeObjectURL(url);
};

export const getOpenPositions = async () => {
  const response = await apiClient.get('/api/algos/open-positions');
  return response.data;
};

export const getAutoPilotStatus = async () => {
  const response = await apiClient.get('/api/autopilot/status');
  return response.data;
};

export const getAutoPilotCandidates = async () => {
  const response = await apiClient.get('/api/autopilot/candidate-positions');
  return response.data;
};

export const startAutoPilot = async (rows) => {
  const response = await apiClient.post('/api/autopilot/start', { rows });
  return response.data;
};

export const stopAutoPilot = async () => {
  const response = await apiClient.post('/api/autopilot/stop');
  return response.data;
};

export const getMyTradesDates = async () => {
  const response = await apiClient.get('/api/my-trades/dates');
  return response.data;
};

export const getMyTrades = async (date) => {
  const response = await apiClient.get('/api/my-trades', { params: date ? { date } : {} });
  return response.data;
};

export const squareOffMyTrade = async (payload) => {
  const response = await apiClient.post('/api/my-trades/square-off', payload);
  return response.data;
};

export default apiClient;
